import { create } from 'zustand';
import { useDshSessionStore, type DshSessionScope } from './useDshSessionStore';
import type { FormDraft, QuestionDraft, TimelineReadingPosition, TimelineReadingPositions } from '@borgee/agent-remote-web/react';

type ConversationView = 'session' | 'messages';
export interface InteractionDraft { question?: QuestionDraft; form?: FormDraft; planFeedback?: string }
export interface ResolvedSessionSelection { owner: string; epoch: number; selector?: string; revision: number; scope: DshSessionScope; title?: string; nativeSessionId?: string; directoryGeneration?: string }
const MAX_CACHED_ITEMS = 128;
const MAX_DRAFT_CHARACTERS = 128 * 1024;

function bounded<T>(values: Record<string, T>): Record<string, T> {
  return Object.fromEntries(Object.entries(values).slice(-MAX_CACHED_ITEMS));
}
interface State {
  views: Record<string, ConversationView>;
  drafts: Record<string, InteractionDraft>;
  attachments: Record<string, number>;
  sessionSelections: Record<string, ResolvedSessionSelection>;
  trackAttachments(key: string, count: number): void;
  rememberSessionSelection(key: string, selection: ResolvedSessionSelection): void;
  select(key: string, view: ConversationView): void;
  edit(key: string, draft: InteractionDraft): void;
  clearDraft(key: string): void;
  retainDrafts(scopeKey: string, tokens: readonly string[]): void;
  clearScope(scopeKey: string): void;
  resetServer(accountId?: string): void;
  clearChannel(channelId: string): void;
  reset(): void;
}

/** Sensitive answers remain in bounded memory, scoped to an authorized request occurrence. */
export const useNormalizedConversationStore = create<State>((set) => ({
  views: {}, drafts: {}, attachments: {}, sessionSelections: {},
  trackAttachments: (key, count) => set(state => {
    const attachments = { ...state.attachments };
    if (count) attachments[key] = count; else delete attachments[key];
    return { attachments };
  }),
  rememberSessionSelection: (key, selection) => set(state => ({ sessionSelections: bounded({ ...state.sessionSelections, [key]: selection }) })),
  select: (key, view) => set(state => ({ views: bounded({ ...state.views, [key]: view }) })),
  edit: (key, draft) => set(state => {
    const drafts = { ...state.drafts }; delete drafts[key];
    // Oversized input remains in the mounted card rather than entering the remount cache.
    if (JSON.stringify(draft).length <= MAX_DRAFT_CHARACTERS) drafts[key] = draft;
    return { drafts: bounded(drafts) };
  }),
  clearDraft: key => set(state => { const drafts = { ...state.drafts }; delete drafts[key]; return { drafts }; }),
  retainDrafts: (scopeKey, tokens) => set(state => {
    const allowed = new Set(tokens.map(token => normalizedQuestionKey(scopeKey, token)));
    const entries = Object.entries(state.drafts).filter(([key]) => JSON.parse(key)[0] !== scopeKey || allowed.has(key));
    return entries.length === Object.keys(state.drafts).length ? state : { drafts: Object.fromEntries(entries) };
  }),
  clearScope: scopeKey => set(state => {
    const views = { ...state.views }; delete views[scopeKey]; normalizedReadingPositions.delete(scopeKey);
    const sessionSelections = { ...state.sessionSelections }; delete sessionSelections[scopeKey];
    return { views, sessionSelections, drafts: Object.fromEntries(Object.entries(state.drafts).filter(([key]) => JSON.parse(key)[0] !== scopeKey)) };
  }),
  clearChannel: channelId => set(state => {
    const keep = (key: string) => JSON.parse(key)[2] !== channelId;
    for (const key of normalizedReadingPositions.keys()) if (!keep(key)) normalizedReadingPositions.delete(key);
    return { views: Object.fromEntries(Object.entries(state.views).filter(([key]) => keep(key))), sessionSelections: Object.fromEntries(Object.entries(state.sessionSelections).filter(([key]) => keep(key))),
      drafts: Object.fromEntries(Object.entries(state.drafts).filter(([key]) => keep(JSON.parse(key)[0]))) };
  }),
  resetServer: accountId => set(state => {
    const keep = (key: string) => { const [account, guild] = JSON.parse(key); return !!accountId && account === accountId && guild === `dm:${accountId}`; };
    for (const key of normalizedReadingPositions.keys()) if (!keep(key)) normalizedReadingPositions.delete(key);
    return { views: Object.fromEntries(Object.entries(state.views).filter(([key]) => keep(key))), sessionSelections: Object.fromEntries(Object.entries(state.sessionSelections).filter(([key]) => keep(key))),
      drafts: Object.fromEntries(Object.entries(state.drafts).filter(([key]) => keep(JSON.parse(key)[0]))), attachments: {} };
  }),
  reset: () => { normalizedReadingPositions.clear(); set({ views: {}, drafts: {}, attachments: {}, sessionSelections: {} }); },
}));

class ReadingPositions extends Map<string, TimelineReadingPosition> {
  override set(key: string, value: TimelineReadingPosition): this {
    super.delete(key); super.set(key, value);
    while (this.size > MAX_CACHED_ITEMS) super.delete(this.keys().next().value!);
    return this;
  }
}
export const normalizedReadingPositions: TimelineReadingPositions = new ReadingPositions();
export const normalizedQuestionKey = (scopeKey: string, token: string): string => JSON.stringify([scopeKey, token]);
export const attachmentDraftKey = (accountId: string, draftKey: string): string => JSON.stringify([accountId, draftKey]);

// Resolve and revoke drafts even while Messages is the mounted conversation view.
const unbindSessionDrafts = useDshSessionStore.subscribe((state, previous) => {
  for (const [key, oldSession] of Object.entries(previous.sessions)) {
    const session = state.sessions[key];
    if (session === oldSession) continue;
    if (!session) useNormalizedConversationStore.getState().clearScope(key);
    else if (session.loaded && !session.loading) useNormalizedConversationStore.getState().retainDrafts(key, session.pending.map(value => value.token));
  }
});
if (import.meta.hot) import.meta.hot.dispose(unbindSessionDrafts);
